import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { api, errMsg, fmtDate } from "@/lib/api";

function ResolveForm({ d, published, onDone }) {
  const r = d.risoluzione;
  const [f, setF] = useState({ outcome_ids: r?.outcome_ids || [], fonte_canonica: r?.fonte_canonica || "", capitolo: r?.capitolo || "",
    data_rivelazione: r?.data_rivelazione || "", motivazione: "", regola_editoriale: r?.regola_editoriale || "" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggle = (id) => setF({ ...f, outcome_ids: f.outcome_ids.includes(id) ? f.outcome_ids.filter((x) => x !== id) : [...f.outcome_ids, id] });
  const submit = async () => {
    try {
      const res = await api.post(`/admin/dossiers/${d.id}/resolve`, { ...f, capitolo: parseInt(f.capitolo, 10) });
      toast.success(`Punti calcolati: ${res.data.riepilogo.corretti}/${res.data.riepilogo.partecipanti} corretti`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-2" data-testid="resolve-form">
      <label className="luma-label">Esito/i confermato/i</label>
      {published.map((t) => (
        <label key={t.id} className="flex items-center gap-2 text-sm"><Checkbox checked={f.outcome_ids.includes(t.id)} onCheckedChange={() => toggle(t.id)} data-testid={`resolve-outcome-${t.id}`} /> {t.titolo}</label>
      ))}
      <div className="grid gap-2 md:grid-cols-3">
        <Input placeholder="Fonte canonica (es. VIZ cap. …)" value={f.fonte_canonica} onChange={set("fonte_canonica")} data-testid="resolve-source-input" />
        <Input type="number" min={1} placeholder="Capitolo" value={f.capitolo} onChange={set("capitolo")} data-testid="resolve-chapter-input" />
        <Input type="date" value={f.data_rivelazione} onChange={set("data_rivelazione")} data-testid="resolve-date-input" />
      </div>
      <Textarea placeholder="Motivazione" value={f.motivazione} onChange={set("motivazione")} rows={2} data-testid="resolve-reason-input" />
      <Input placeholder="Regola editoriale (obbligatoria se più esiti compatibili)" value={f.regola_editoriale} onChange={set("regola_editoriale")} data-testid="resolve-rule-input" />
      <Button onClick={submit} className="rounded-full bg-pink-500 text-white hover:bg-pink-400" data-testid={`resolve-mystery-button-${d.id}`}>{r ? "Rettifica risoluzione e ricalcola" : "Risolvi e calcola punti"}</Button>
    </div>
  );
}

export function ResolvePanel({ data, onChange }) {
  const d = data.dossier;
  const [motivo, setMotivo] = useState("");
  const published = data.teorie.filter((t) => t.stato === "pubblicata");
  const call = async (fn, ok) => { try { await fn(); toast.success(ok); onChange(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <section className="luma-card space-y-5" data-testid="resolve-panel">
      <h2 className="text-lg font-semibold">Chiusura, risoluzione e punteggi</h2>
      <p className="text-xs text-muted-foreground">100 punti se corretto, +30 se l'esito era scelto da meno del 20% dei pronostici validi allo snapshot. Lo snapshot usa l'ultima scelta di ciascuna identità prima della chiusura o della data di rivelazione (la più vecchia). Ricalcolo idempotente e registrato in audit.</p>
      {d.esito === "aperto" && d.stato === "pubblicato" && (
        <Button variant="outline" className="rounded-full" onClick={() => call(() => api.post(`/admin/dossiers/${d.id}/close`), "Pronostici chiusi")} data-testid="close-votes-button">Chiudi pronostici (congela snapshot)</Button>
      )}
      {d.stato === "pubblicato" && published.length >= 2 && <ResolveForm key={d.risoluzione?.versione || 0} d={d} published={published} onDone={onChange} />}
      {d.esito !== "aperto" && (
        <div className="flex flex-wrap gap-2">
          <Input placeholder="Motivo della riapertura" value={motivo} onChange={(e) => setMotivo(e.target.value)} className="max-w-md" data-testid="reopen-reason-input" />
          <Button variant="ghost" className="rounded-full" onClick={() => call(() => api.post(`/admin/dossiers/${d.id}/reopen`, { motivo }), "Mistero riaperto")} data-testid="reopen-button">Riapri mistero</Button>
        </div>
      )}
      {data.snapshot && (
        <p className="text-xs" data-testid="snapshot-info">Snapshot: {data.snapshot.totale} pronostici validi al {fmtDate(data.snapshot.cutoff)} · {JSON.stringify(data.snapshot.conteggi)}</p>
      )}
      {data.punteggi.length > 0 && (
        <ul className="space-y-1 text-xs" data-testid="score-entries">
          {data.punteggi.map((s) => <li key={s.identity_key}>{s.identity_key} → {s.theory_id}: <b>{s.totale}</b> ({s.punti_base}+{s.bonus}) v{s.versione}</li>)}
        </ul>
      )}
      {d.storico_risoluzioni?.length > 0 && (
        <ul className="space-y-1 text-xs text-muted-foreground" data-testid="resolution-history">
          {d.storico_risoluzioni.map((h, i) => <li key={i}>{h.tipo} · v{h.versione} · esiti {h.outcome_ids?.join(", ")} · cap. {h.capitolo} · {fmtDate(h.sostituita_at)} {h.motivo || ""}</li>)}
        </ul>
      )}
    </section>
  );
}
